import {
  type FieldMetadata,
  getTextareaProps,
  useInputControl,
} from "@conform-to/react";
import { type ChangeEventHandler, useState } from "react";
import type {
  Country as PhoneCountry,
  Value as PhoneValue,
} from "react-phone-number-input";

import { cn } from "../lib/cn";
import { Input } from "./input";
import { Label } from "./label";
import { PhoneInput } from "./phone-input";
import { Textarea } from "./textarea";

export interface FormInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "className"> {
  id: string;
  name: string;
  label: string;
  errors?: Array<string>;
  hideLabel?: boolean;
  inputClassName?: string;
  showRequiredIndicator?: boolean;
}

export function FormInput(props: FormInputProps) {
  const {
    label,
    errors,
    hideLabel,
    inputClassName,
    showRequiredIndicator = true,
    type: propType,
    ...rest
  } = props;
  const hasErrors = errors && errors.length > 0;
  const [type, setType] = useState(propType);

  return (
    <div className="relative">
      <div className={cn("mb-2", hideLabel && "sr-only")}>
        <Label
          htmlFor={rest.id}
          className={cn(
            "font-medium text-sm text-twi-gray-400 leading-[150%]",
            props.disabled && "cursor-not-allowed opacity-50",
          )}
        >
          {label}
          {props.required && showRequiredIndicator ? (
            <span className="text-twi-red-600"> *</span>
          ) : null}
        </Label>
      </div>
      <Input
        variant="form"
        error={hasErrors}
        className={inputClassName}
        aria-describedby={
          !hasErrors ? `${props.id}-description` : props["aria-describedby"]
        }
        type={type}
        {...rest}
      />
      {propType === "password" ? (
        <button
          data-testid="form-button-show-password"
          type="button"
          className="absolute -top-0.5 right-0 rounded p-2 text-muted-foreground text-xs underline transition focus:outline-none focus-visible:ring focus-visible:ring-primary/50"
          onClick={() =>
            setType((t) => (t === "password" ? "text" : "password"))
          }
        >
          {type === "password" ? "Show" : "Hide"}
        </button>
      ) : null}
      {hasErrors ? (
        <div className="mt-2">
          <FieldErrors id={rest.id} errors={errors} />
        </div>
      ) : null}
    </div>
  );
}

export interface FormPhoneInputProps {
  fieldId: string;
  fieldName: string;
  label: string;
  errors?: Array<string>;
  autoFocus?: boolean;
  value?: string;
  onChange?: (value: PhoneValue) => void;
  placeholder?: string;
  autoComplete?: string;
  inputMode?:
    | "tel"
    | "text"
    | "none"
    | "search"
    | "email"
    | "numeric"
    | "decimal"
    | "url";
  "data-testid"?: string;
  required?: boolean;
  showRequiredIndicator?: boolean;
  disabled?: boolean;
  onCountryChange?: (country: PhoneCountry) => void;
  inputWrapperClassName?: string;
}

export function FormPhoneInput(props: FormPhoneInputProps) {
  const {
    fieldId,
    fieldName,
    label,
    errors,
    value,
    onChange,
    required,
    showRequiredIndicator = true,
    disabled,
    onCountryChange,
    inputWrapperClassName,
    ...rest
  } = props;
  const hasErrors = errors && errors.length > 0;

  return (
    <div className="relative">
      <div className="mb-2">
        <Label
          htmlFor={fieldId}
          className={cn(
            "font-medium text-sm text-twi-gray-400 leading-[150%]",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          {label}
          {required && showRequiredIndicator ? (
            <span className="text-twi-red-400"> *</span>
          ) : null}
        </Label>
      </div>
      <input
        type="hidden"
        name={fieldName}
        value={value}
        data-testid="form-phone-hidden-input"
      />
      <PhoneInput
        id={fieldId}
        value={value}
        onChange={onChange}
        onCountryChange={onCountryChange}
        className={cn(inputWrapperClassName, hasErrors && "border-twi-red-400")}
        disabled={disabled}
        {...rest}
      />
      {hasErrors ? (
        <div className="mt-2">
          <FieldErrors id={fieldId} errors={errors} />
        </div>
      ) : null}
    </div>
  );
}

export interface FormTextareaProps
  extends Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    keyof FieldMetadata<string>
  > {
  meta: FieldMetadata<string>;
  label: string;
  errors: Array<string> | undefined;
  description?: string;
  hideLabel?: boolean;
}

export function FormTextarea(props: FormTextareaProps) {
  const { label, errors, hideLabel, description, className, meta, ...rest } =
    props;
  const hasErrors = errors?.length;
  const control = useInputControl(meta);
  const { key, ...metaProps } = getTextareaProps(meta);

  return (
    <div className="relative">
      <Label
        htmlFor={metaProps.id}
        className={cn(
          hideLabel ? "sr-only" : "mb-1.5",
          hasErrors && "text-destructive",
          props.disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <span>{label}</span>
        <span
          className={cn(
            "ml-1 inline-block font-normal",
            metaProps.required || hasErrors
              ? "text-destructive"
              : "text-muted-foreground",
            !metaProps.required && "text-xs",
          )}
        >
          {metaProps.required ? "*" : "(optional)"}
        </span>
      </Label>
      <Textarea
        key={key}
        onFocus={control.focus}
        onBlur={control.blur}
        onChange={
          control.change as unknown as ChangeEventHandler<HTMLTextAreaElement>
        }
        className={cn(
          hasErrors &&
            "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/50",
          className,
        )}
        aria-describedby={
          !hasErrors && description
            ? `${metaProps.id}-description`
            : props["aria-describedby"]
        }
        rows={4}
        {...rest}
        {...metaProps}
      />
      {description || hasErrors ? (
        <div className="mt-0.5 ml-0.5">
          <FieldErrors id={metaProps.id} errors={errors} />
        </div>
      ) : null}
    </div>
  );
}

export function FieldErrors({
  id,
  errors,
}: {
  id: string;
  errors?: Array<string>;
}) {
  if (!errors?.length) {
    return null;
  }

  return (
    <p
      aria-live="polite"
      id={`${id}-error`}
      className="mb-1 font-medium text-twi-red-400 text-xs"
    >
      {errors.map((error) => (
        <span key={error} className="block">
          {error}.
        </span>
      ))}
    </p>
  );
}
